import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'

export type AuditLog = {
    _id: string
    adminUserId: {
        _id: string
        name: string
        email: string
    }
    adminEmail: string
    action: string
    targetType?: string
    targetId?: string
    targetOrganizationId?: {
        _id: string
        name: string
    }
    previousValues?: Record<string, unknown>
    newValues?: Record<string, unknown>
    /** targetLabel names the record in words, so an entry survives it being renamed or deleted. */
    metadata?: { targetLabel?: string }
    ipAddress?: string
    userAgent?: string
    createdAt: string
}

export type AuditLogsResponse = {
    success: boolean
    data: {
        logs: AuditLog[]
        pagination: {
            page: number
            limit: number
            total: number
            totalPages: number
        }
    }
}

export type AuditLogsFilters = {
    page?: number
    limit?: number
    action?: string
    adminUserId?: string
    targetOrganizationId?: string
    startDate?: string
    endDate?: string
}

const FIVE_MINUTES = 1000 * 60 * 5

export const useAdminAuditLogsQuery = (filters: AuditLogsFilters = {}) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    const getAuditLogs = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            const params = new URLSearchParams()

            if (filters.page) params.append('page', String(filters.page))
            if (filters.limit) params.append('limit', String(filters.limit))
            if (filters.action) params.append('action', filters.action)
            if (filters.adminUserId) params.append('adminUserId', filters.adminUserId)
            if (filters.targetOrganizationId) params.append('targetOrganizationId', filters.targetOrganizationId)
            if (filters.startDate) params.append('startDate', filters.startDate)
            if (filters.endDate) params.append('endDate', filters.endDate)

            const response = await callAPI(`/api/admin/audit-logs?${params.toString()}`, 'get', token)
            return response
        } catch (e) {
            console.error('Error fetching audit logs:', e)
            throw e
        }
    }

    return useQuery<AuditLogsResponse>(['admin', 'audit-logs', filters], getAuditLogs, {
        enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES,
        staleTime: 30000,
        retry: 3,
        keepPreviousData: true,
    })
}
